/**
 * TestedAssetStack — the "what was tested" affordance in the report hero eyebrow.
 *
 * One text button carrying the count label ("3 concepts", "1 ad"). It opens the
 * preview dialog on the first asset in item-letter order; the dialog's rail is
 * where the reader picks a specific asset. Tiles were dropped from the eyebrow
 * on purpose (Fernando 2026-09-13): the row reads as one line of text.
 */
import { useTranslation } from 'react-i18next'

import { cn } from '@/lib/utils'

import {
  assetCountLabelKey,
  sortAssetsByItemTag,
} from '@/features/studies/report/utils/testedAssets'
import { studyAssetType } from '@/features/studies/questionnaire/assets/conceptAssets'

import { TestedAsset, TestedAssetBlock } from '@digital-twins/shared-types'

import { assetEyebrowIcon, eyebrowIconClass, eyebrowItemClass } from './eyebrowMeta'

export interface TestedAssetStackProps {
  assets: Array<TestedAsset>
  itemTags: Record<string, string>
  onOpen: (assetId: string) => void
}

export function TestedAssetStack(props: TestedAssetStackProps) {
  const { assets, itemTags, onOpen } = props

  const { t } = useTranslation('app')

  const sortedAssets = sortAssetsByItemTag(assets, itemTags)
  const label = t(assetCountLabelKey(assets), { count: assets.length })
  const icon = assetEyebrowIcon(studyAssetType({ assets } as TestedAssetBlock))
  const AssetIcon = icon.Icon

  const onClick = () => {
    const first = sortedAssets[0]
    if (first) {
      onOpen(first.id)
    }
  }

  if (sortedAssets.length === 0) {
    return null
  }

  return (
    <button
      type="button"
      data-testid="report-asset-stack"
      aria-label={label}
      onClick={onClick}
      className={cn(
        eyebrowItemClass,
        "rounded-md underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
      )}
    >
      <AssetIcon
        className={eyebrowIconClass}
        aria-hidden="true"
        data-testid="report-asset-type-icon"
        data-icon={icon.name}
      />
      <span data-testid="report-asset-count">{label}</span>
    </button>
  )
}
